import type { ComponentChildren } from "preact";
import type { TerminalState, LeadPriority, CloseReason, DashboardAlert } from "../../types";

export type BadgeVariant =
  | "neutral"
  | "success"
  | "warn"
  | "danger"
  | "info"
  | "highlight";

interface BadgeProps {
  variant?: BadgeVariant;
  onDark?: boolean;
  children: ComponentChildren;
}

export function Badge({ variant = "neutral", onDark, children }: BadgeProps) {
  const cls = [
    "badge",
    `badge--${variant}`,
    onDark ? "badge--on-dark" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return <span class={cls}>{children}</span>;
}

// ---- Variant maps — fontes únicas pra estado terminal e prioridade ----

const STATE_VARIANT: Record<TerminalState, BadgeVariant> = {
  success: "success",
  conflict: "danger",
  soft: "warn",
  abuse: "neutral",
};
const STATE_LABEL: Record<TerminalState, string> = {
  success: "Sucesso",
  conflict: "Conflict",
  soft: "Soft disengage",
  abuse: "Abuse",
};

const PRIORITY_VARIANT: Record<LeadPriority, BadgeVariant> = {
  hot: "highlight",
  warm: "warn",
  cold: "info",
};

export function StateBadge(
  { state, onDark }: { state?: TerminalState | null; onDark?: boolean },
) {
  if (!state) return <Badge onDark={onDark}>—</Badge>;
  return (
    <Badge variant={STATE_VARIANT[state]} onDark={onDark}>
      {STATE_LABEL[state] ?? state}
    </Badge>
  );
}

function stateIconPath(state: TerminalState): string {
  if (state === "success") return "M9.4 16.6 4.8 12l-1.4 1.4 6 6 11-11L19 7 9.4 16.6Z";
  if (state === "conflict") return "M11 4h2v10h-2V4Zm0 12h2v2h-2v-2Z M12 2 1 21h22L12 2Z";
  if (state === "abuse") return "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20ZM6 12a6 6 0 0 1 9.3-5L7 15.3A6 6 0 0 1 6 12Zm6 6a6 6 0 0 1-3.3-1L17 8.7A6 6 0 0 1 12 18Z";
  return "M6 5h12v14H6V5Zm2 2v10h8V7H8Zm2 3h4v2h-4v-2Z";
}

function stateIconLabel(state: TerminalState, closeReason?: CloseReason | null): string {
  if (state === "soft" && closeReason === "manual_close") return "Fechada pelo portal";
  return STATE_LABEL[state] ?? state;
}

export function StateIconBadge(
  { state, closeReason }: { state?: TerminalState | null; closeReason?: CloseReason | null },
) {
  if (!state) return null;
  const label = stateIconLabel(state, closeReason);
  return (
    <span
      class={`badge badge--${STATE_VARIANT[state]} badge--icon`}
      title={label}
      aria-label={label}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d={stateIconPath(state)} />
      </svg>
    </span>
  );
}

function alertVariant(alerts?: DashboardAlert[] | null): BadgeVariant {
  if (alerts?.some((alert) => alert.level === "danger")) return "danger";
  if (alerts?.some((alert) => alert.level === "warn")) return "warn";
  return "info";
}

function alertIconPath(variant: BadgeVariant): string {
  if (variant === "danger") return "M12 2 2 7v10l10 5 10-5V7L12 2Zm-1 5h2v7h-2V7Zm0 9h2v2h-2v-2Z";
  if (variant === "info") return "M11 10h2v8h-2v-8Zm0-4h2v2h-2V6Zm1-4a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z";
  return "M12 2 1 21h22L12 2Zm-1 7h2v6h-2V9Zm0 8h2v2h-2v-2Z";
}

function alertLabel(count: number, alerts?: DashboardAlert[] | null): string {
  if (count <= 0) return "Sem alertas";
  const first = alerts?.[0];
  const suffix = first?.label ? `: ${first.label}` : "";
  return `${count} ${count === 1 ? "alerta" : "alertas"}${suffix}`;
}

export function AlertIconBadge(
  { count, alerts }: { count?: number | null; alerts?: DashboardAlert[] | null },
) {
  const total = count || 0;
  if (total <= 0) return null;
  const variant = alertVariant(alerts);
  const label = alertLabel(total, alerts);
  return (
    <span
      class={`badge badge--${variant} badge--icon badge--alert-icon`}
      title={label}
      aria-label={label}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d={alertIconPath(variant)} />
      </svg>
      {total > 1 && <span class="badge-icon-count">{total}</span>}
    </span>
  );
}

export function PriorityBadge(
  { priority }: { priority?: LeadPriority | null },
) {
  if (!priority) return <Badge>—</Badge>;
  return <Badge variant={PRIORITY_VARIANT[priority]}>{priority}</Badge>;
}
